import {connect} from 'pwa-helpers';
import {LitElement, html, css} from 'lit';
import {customElement} from 'lit/decorators/custom-element.js';
import {msg} from '@lit/localize';
import {when} from 'lit/directives/when.js';
import {conditional} from '../../../util/conditional';
import store from '../../../store';
import {filterAndJoin} from '../../../util/filterAndJoin';
import {getValueOfFeature, pimText, pimTextDebug} from '../../../util/values';
import {
	selectRestrictionDimmingFactorBySeason,
	selectSolarActivityLevelData,
	selectSolarAzimuthData, selectSolarCalculated,
	selectSolarDimmProfileData,
	selectSolarLuminarCountValue,
	selectSolarLumPosition1,
	selectSolarLumPosition2, selectSolarSelfSufficiency,
	selectSolarShadowLevelData, selectSolarUnitHeight, selectSolarVersion
} from '../../solarSelectors';
import {getLumen, getPower} from '../../../util/solarOutput';
import {selectSpcConfiguration} from '../../../selectors';

@customElement('solar-technical-data')
export class SolarTechnicalData extends connect(store)(LitElement) {

	static styles = [
		css`
	:host {
		margin: 0 var(--specsheet-large-gap) var(--specsheet-medium-gap) var(--specsheet-large-gap);
	}

	.summary {
		display: flex;
		justify-content: space-between;
		width: 100%;
		padding-top: var(--specsheet-medium-gap);
	}


	.description {
		width: 100%;
	}

	.imageContainer {
		text-align: center;
		width: 50%;
		height: 95%;
		max-height: 870px;
		margin-left: auto;
		margin-right: auto;
	}

	@-moz-document url-prefix(){
		.imageContainer {
			text-align: center;
			width: 20vw;
			max-height: 770px;
			height: 85%;
			margin-bottom: 10px;
		}
	}


	@media print {
		:host {
			margin: 0;
		}

		.summaryContainer {
			width: 30%;
		}

		.description {
			width: 70%;
		}
	}
	`]
	;

	constructor() {
		super();
		store.subscribe(() => this.requestUpdate());
	}

	stateChanged(state) {
		this.state = state;
		this.productData = state.productData;
		this.status = state.status;
		this.debug = this.status.debug;
		this.lang = state.status.lang;
		this.spcConfiguration = selectSpcConfiguration(state);
		this.conditionalVariantValues = state.productData.conditionalVariantValues;
		this.solar = state.solar.data;
		this.generators =  parseInt(selectSolarUnitHeight(state), 10);
		this.location = this.solar.project.location;
		this.orientation = this.solar.project.orientation;
		this.selfSufficiency =  selectSolarSelfSufficiency(state);
		this.solarVersion = selectSolarVersion(state);
		this.isPro = selectSolarVersion(state) === 'AetoPro';
		this.isBasic = selectSolarVersion(state) === 'AetoBasic';

		if (this.location && this.orientation) {
			this.lumCount = selectSolarLuminarCountValue(state) ?? 1;
			this.restrictionDimmingFactorBySeason = selectRestrictionDimmingFactorBySeason(this.state);
		}
		this.baseHeight = parseInt(getValueOfFeature( 'HS_HousingHeight', this.spcConfiguration), 10) / 10;
		this.fullHeight = this.baseHeight + this.generators;
		this.lum1Height = this.baseHeight + parseInt(selectSolarLumPosition1(state), 10);
		this.lum2Height = this.baseHeight + parseInt(selectSolarLumPosition2(state), 10);

		this.calculated = selectSolarCalculated(state);
		this.shortNumberProductModel = state.shortNumber.data.ProductModel ?? '';
	}

	t(featureId, context, value, prefix, suffix) {
		return this.debug ?
			pimTextDebug(this.lang, featureId, context, this.spcConfiguration, this.state.productData.translations, value, prefix, suffix) :
			pimText(this.lang, featureId, context, this.spcConfiguration, this.state.productData.translations, value, prefix, suffix);
	}
	c(id) {
		return conditional(this.spcConfiguration, this.conditionalVariantValues, id);
	}


	render() {
		const s = this.state;
		if (!this.location || !this.orientation || !this.restrictionDimmingFactorBySeason) return null;
		const shouldDisplayFeature = (value) => value !== undefined && value !== '';
		const descAtt = this.t(this.c('DescriptiveAttributeReference.LE_CRI'), 'DV');

		const applicableStandards = [
			this.t('LU_IP.IP66', 'DV.S'),
			this.t('LU_IK.10', 'DV.S'),
			this.t('EB_ElectricalProtectionClass_1', 'FV.S'),
			this.t('LU_ENEC_CertificateDescription.ENEC', 'DV.S'),
			this.t('LU_CE.CE', 'DV.S'),
		  ];
		  const filteredStandards = applicableStandards.filter(value => value !== undefined && value !== '');
		  const isStandardsPresent = filteredStandards.length > 0;

		const criDescAtt = this.t(this.c('DescriptiveAttributeReference.LE_CRI'), 'DV.S');

		return html`
			<div class="summary">

				<div class="description">

					<solar-specsheet-feature>
						<trans-label slot="label" key="HS_HousingType" context="F"></trans-label>
						<span slot="value">
								${filterAndJoin([
									this.t('HS_HousingSize', 'FV.S', null, '', ''),
									this.t('HS_HousingType', 'FV.S', null, ' ', ''),
									this.t('HS_Construction', 'FV.S', null, ', '),
								], '')}
							</span>
					</solar-specsheet-feature>
					<solar-specsheet-feature class="specsheet">
						<span slot="label">${msg('Location')}</span>
						<span slot="value">${this.location?.name}</span>
					</solar-specsheet-feature>
					<solar-specsheet-feature class="specsheet">
						<span slot="label">${msg('Orientation')}</span>
						<span slot="value">${selectSolarAzimuthData(this.state).label[this.lang]}</span>
					</solar-specsheet-feature>
					<solar-specsheet-feature class="specsheet">
						<span slot="label">${msg('Shading')}</span>
						<span slot="value">${selectSolarShadowLevelData(this.state).label[this.lang]}</span>
					</solar-specsheet-feature>
					<solar-specsheet-feature class="specsheet">
						<span slot="label">${msg('Lighting profile')}</span>
						<span slot="value">${selectSolarDimmProfileData(this.state).label[this.lang]}</span>
					</solar-specsheet-feature>
					<solar-specsheet-feature class="specsheet">
						<span slot="label">${msg('Self Sufficient Days')}</span>
						<span slot="value">${when(this.isPro,
							() => html`
								${msg('Summer')}: ${this.selfSufficiency[this.solarVersion].summer},
								${msg('Spring/Fall')}: ${this.selfSufficiency[this.solarVersion].spring},
								${msg('Winter')}: ${this.selfSufficiency[this.solarVersion].winter}
							`,
							() => html`
								${this.selfSufficiency[this.solarVersion].summer}
							`
						)}
						</span>
					</solar-specsheet-feature>

					<solar-specsheet-feature>
						<trans-label slot="label" key="EB_MotionSensor_1" context="F"></trans-label>
						<span slot="value">
								${filterAndJoin([
									selectSolarActivityLevelData(this.state).label[this.lang],
									selectSolarActivityLevelData(this.state).description[this.lang]
								], ', ')}
							</span>
					</solar-specsheet-feature>
					<solar-specsheet-feature>
						<trans-label slot="label" key="LU_SolarUnit" context="F"></trans-label>
						<trans-label slot="value" key="LU_SolarUnit" context="FV.S"></trans-label>
						<br>
					</solar-specsheet-feature>
					<solar-specsheet-feature>
						<trans-label slot="label" key="LE_ColourTemperature_1" context="F"></trans-label>
						<trans-label slot="value" key="LE_ColourTemperature_1" context="FV.S" prefix="LED "></trans-label>
						<br>
					</solar-specsheet-feature>
					<solar-specsheet-feature class="specsheet">
							<span slot="label">
								${msg('Power')} (max)
							</span>
						<span slot="value">
							${when(
								this.isBasic,
								() => html`
									${msg('Full Year Profile (max)')}: ${this.lumCount > 1 ? this.lumCount + 'x' : ''} ${this.calculated.winter.power} W, ${this.lumCount > 1 ? this.lumCount + 'x' : ''} ${this.calculated.winter.lum} lm
									<br>
								`,
								() => html`
									${msg('Summer')}: ${this.lumCount > 1 ? this.lumCount + 'x' : ''} ${this.calculated.summer.power} W, ${this.lumCount > 1 ? this.lumCount + 'x' : ''} ${this.calculated.summer.lum} lm
									<br>
									${msg('Autumn')}: ${this.lumCount > 1 ? this.lumCount + 'x' : ''} ${this.calculated.spring.power} W, ${this.lumCount > 1 ? this.lumCount + 'x' : ''} ${this.calculated.spring.lum} lm
									<br>
									${msg('Winter')}: ${this.lumCount > 1 ? this.lumCount + 'x' : ''} ${this.calculated.winter.power} W, ${this.lumCount > 1 ? this.lumCount + 'x' : ''} ${this.calculated.winter.lum} lm
									<br>
								`)}
							</span>
					</solar-specsheet-feature>
					<solar-specsheet-feature>
						<trans-label slot="label" key="LE_LightDistribution_1" context="F"></trans-label>
						<span slot="value">
							<trans-label key="LE_LightDistribution_1" context="FV.S" prefix="LED "></trans-label><br>
							${when(this.lumCount > 1, () => html`
								<trans-label key="LE_LightDistribution_2" context="FV.S" prefix="LED "></trans-label>
							`)}
						</span>
					</solar-specsheet-feature>
					<solar-specsheet-feature>
						<trans-label slot="label" key="HS_LightEmission" context="F"></trans-label>
						<trans-label slot="value" key="HS_LightEmission" context="FV"></trans-label>
						<br>
					</solar-specsheet-feature>
					<solar-specsheet-feature condition="${criDescAtt}">
						<trans-label slot="label" class="label" key="LE_CRI" context="D"></trans-label>
						<span slot="value" class="value">${criDescAtt}</span>
					</solar-specsheet-feature>

					<solar-specsheet-feature>
						<trans-label slot="label" key="LU_Battery" context="F"></trans-label>
						<trans-label slot="value" key="LU_Battery" context="FV.S"></trans-label>
						<br>
					</solar-specsheet-feature>

					<solar-specsheet-feature>
						<trans-label slot="label" key="HS_HousingHeight" context="F"></trans-label>
						<trans-label slot="value" key="HS_HousingHeight" context="FV.S"></trans-label>
						<br>
					</solar-specsheet-feature>
					<solar-specsheet-feature>
						<span slot="label">${msg('Total Height')}</span>
						<span slot="value">${this.fullHeight.toLocaleString()} m</span>
					</solar-specsheet-feature>
					<solar-specsheet-feature>
						<span slot="label">${msg('Luminaire Height')}</span>
						<span slot="value">
								${filterAndJoin([
									this.lumCount > 1 ? this.lum2Height.toLocaleString() + ' m' : null,
									this.lum1Height.toLocaleString() + ' m',
								], ' & ')}
							</span>
					</solar-specsheet-feature>
					<solar-specsheet-feature>
						<trans-label slot="label" key="HS_Base" context="F"></trans-label>
						<trans-label slot="value" key="HS_Base" context="FV.S"></trans-label>
						<br>
					</solar-specsheet-feature>
					<solar-specsheet-feature>
						<trans-label slot="label" key="HS_HousingColour" context="F"></trans-label>
						<trans-label slot="value" key="HS_HousingColour" context="FV.S"></trans-label>
						<br>
					</solar-specsheet-feature>
					${this.shortNumberProductModel ? html`
					<solar-specsheet-feature>
						<span slot="label" class="label">${msg('Model ID')}</span>
						<span slot="value" class="value">
								${this.shortNumberProductModel}
						</span>
					</solar-specsheet-feature>` : ''}

				</div>
		`;
	}
}
